/**
 * Walk tool — author the walking path directly on the scene. Sub-modes:
 *
 *   FLOOR (3 clicks): each click GPU-picks the splat under the cursor
 *     (`camera.intersect`) for an accurate point ON the floor. After 3 valid
 *     picks WalkArea builds a deterministic plane and we auto-advance to…
 *
 *   BOUNDARY: each click casts the camera ray onto the ACTIVE level's plane
 *     (`rayPlane`) and drops a boundary vertex there — coplanar by
 *     construction. Add vs. move is decided by INTENT:
 *       • click (press + release, no drag)  → ADD a vertex at the cursor
 *         (on an existing edge: INSERT it into that edge)
 *       • press on a vertex + drag (>4px)    → MOVE that vertex
 *       • right-click a vertex, or Delete while over it → REMOVE it
 *       • press on a stairs corner + drag    → MOVE that corner (XZ; its
 *         height stays the flight's end height)
 *     so you can place a large number of points without an accidental press
 *     near an existing vertex hijacking the click into a drag, and edit a
 *     finished outline without redrawing it.
 *
 *   LEVEL-HEIGHT (1 click): GPU-picks a point ON an upper floor to set the
 *     active level's height, then auto-advances to BOUNDARY for that level.
 *
 *   STAIRS (4 clicks): each click raycasts onto the DRAWN floor planes (not
 *     the splats) — click a point on one floor, then its opposite point on
 *     another floor: that sloped line is one SIDE of the flight. Repeat for
 *     the other side; the quad between the two side lines is the stairs,
 *     with end heights taken exactly from the two levels. Which floors the
 *     clicks land on decides which two levels the flight connects.
 *
 * Near-grazing camera rays are rejected (a shallow angle to the floor would
 * send the intersection off toward the horizon — a vertex hundreds of metres
 * away). No auto-detection anywhere: the geometry is exactly what you click.
 *
 * Activated via ToolManager (`tool.walk`). useCapture on pointerdown so the
 * camera controller can't steal the click for an orbit-start.
 */

import { Container } from '@playcanvas/pcui';
import { Ray, Vec3 } from 'playcanvas';

import type { Events } from '../../events';
import type { Scene } from '../../scene';
import type { WalkArea } from '../walk-area';
import {
    rayPlane, raycastWalkSurfaces, nearestOnPolygonEdge, planeHeightAt, horizontalPlane,
    type Vec3 as V3, type WalkSurface
} from '../walk-geometry';

type WalkToolMode = 'floor' | 'boundary' | 'level-height' | 'stairs';

const tmpRay = new Ray();

// Reject rays whose angle to the floor is shallower than this (|ray·normal|).
// 0.05 ≈ ~3° of depression; below it the plane hit runs to the horizon.
const MIN_RAY_DOT = 0.05;
// Pixels of pointer movement before a press on a vertex becomes a drag.
const DRAG_THRESH = 4;
// Screen radius (px) within which a press is considered "on" a vertex.
const VERTEX_GRAB_PX = 12;
// Screen distance (px) within which a click on an edge inserts into it.
const EDGE_GRAB_PX = 8;

class WalkTool {
    activate: () => void;
    deactivate: () => void;

    constructor(events: Events, scene: Scene, _canvasContainer: Container) {
        const canvas = scene.canvas;
        let active = false;
        let mode: WalkToolMode = 'floor';

        // Boundary press state (add-vs-drag disambiguation).
        let pointerDown = false;
        let dragging = false;
        let downX = 0, downY = 0;
        let candidateVertex = -1;
        let candidateCorner: { stairs: number; corner: number } | null = null;
        let hoverVertex = -1;

        const walk = (): WalkArea | undefined => events.invoke('walk') as WalkArea | undefined;

        const setMode = (m: WalkToolMode) => {
            if (mode === 'stairs' && m !== 'stairs') events.fire('walk.stairs.cancel');
            mode = m;
            events.fire('walk.tool.mode', m);
        };

        /** The mode that makes sense for the current authoring state. */
        const initialMode = (): WalkToolMode => {
            const wa = walk();
            if (!wa?.hasFloor) return 'floor';
            if (wa.activeLevel > 0 && wa.activeHeight() === null) return 'level-height';
            return 'boundary';
        };

        /**
         * Cast the camera ray (canvas-pixel coords) onto the ACTIVE level's
         * plane. Returns null when there's no plane or the ray is too grazing.
         */
        const hitPlane = (clientX: number, clientY: number): Vec3 | null => {
            const wa = walk();
            const plane = wa?.activePlane();
            if (!plane) return null;
            const rect = canvas.getBoundingClientRect();
            scene.camera.getRay(clientX - rect.left, clientY - rect.top, tmpRay);
            const rd: V3 = [tmpRay.direction.x, tmpRay.direction.y, tmpRay.direction.z];
            const n = plane.normal;
            const dot = rd[0] * n[0] + rd[1] * n[1] + rd[2] * n[2];
            if (Math.abs(dot) < MIN_RAY_DOT) return null; // too grazing → horizon
            const ro: V3 = [tmpRay.origin.x, tmpRay.origin.y, tmpRay.origin.z];
            const hit = rayPlane(ro, rd, plane);
            return hit ? new Vec3(hit[0], hit[1], hit[2]) : null;
        };

        /** Cast the camera ray onto the level plane at height `y` (dragging a stairs corner). */
        const hitHeight = (clientX: number, clientY: number, y: number): Vec3 | null => {
            const rect = canvas.getBoundingClientRect();
            scene.camera.getRay(clientX - rect.left, clientY - rect.top, tmpRay);
            const rd: V3 = [tmpRay.direction.x, tmpRay.direction.y, tmpRay.direction.z];
            if (Math.abs(rd[1]) < MIN_RAY_DOT) return null;
            const hit = rayPlane([tmpRay.origin.x, tmpRay.origin.y, tmpRay.origin.z], rd, horizontalPlane(y));
            return hit ? new Vec3(hit[0], hit[1], hit[2]) : null;
        };

        /** GPU depth-pick a real point on the splats; null when nothing there. */
        const pickSplat = async (clientX: number, clientY: number): Promise<Vec3 | null> => {
            const rect = canvas.getBoundingClientRect();
            const nx = (clientX - rect.left) / rect.width;
            const ny = (clientY - rect.top) / rect.height;
            try {
                const hit = await scene.camera.intersect(nx, ny);
                return hit?.position ?? null;
            } catch (err) {
                return null;
            }
        };

        /**
         * Raycast the cursor against the DRAWN floor planes (every complete
         * level's plane, clipped to its boundary polygon) and return the
         * nearest top-side hit + which level it is. The point then SNAPS to
         * the nearest point on that level's boundary outline — for now the
         * stairs come from the SIDE of the delimited walking area, so flight
         * corners sit exactly on the polygon edge at that floor's height.
         */
        const pickLevelPoint = (clientX: number, clientY: number): { level: number; point: Vec3 } | null => {
            const wa = walk();
            if (!wa) return null;
            const levels = wa.pickableLevelSurfaces();
            if (levels.length === 0) return null;
            const rect = canvas.getBoundingClientRect();
            scene.camera.getRay(clientX - rect.left, clientY - rect.top, tmpRay);
            const ro: V3 = [tmpRay.origin.x, tmpRay.origin.y, tmpRay.origin.z];
            const rd: V3 = [tmpRay.direction.x, tmpRay.direction.y, tmpRay.direction.z];
            const surfaces: WalkSurface[] = levels.map(l => ({ plane: l.plane, poly: l.poly, kind: 'level' }));
            const hit = raycastWalkSurfaces(ro, rd, surfaces);
            if (!hit) return null;
            // Grazing guard, same reasoning as hitPlane: a near-parallel ray
            // "hits" the plane far off toward the horizon.
            const s = levels[hit.surface];
            const n = s.plane.normal;
            if (Math.abs(rd[0] * n[0] + rd[1] * n[1] + rd[2] * n[2]) < MIN_RAY_DOT) return null;
            const edge = nearestOnPolygonEdge(hit.point[0], hit.point[2], s.poly);
            return {
                level: s.level,
                point: new Vec3(edge.x, planeHeightAt(s.plane, edge.x, edge.z), edge.z)
            };
        };

        const onPointerDown = async (e: PointerEvent) => {
            if (!active) return;
            // right-click on a vertex removes it
            if (e.button === 2 && mode === 'boundary') {
                const wa = walk();
                const rect = canvas.getBoundingClientRect();
                const v = wa ? wa.nearestVertex(e.clientX - rect.left, e.clientY - rect.top, VERTEX_GRAB_PX) : -1;
                if (v >= 0) {
                    e.stopPropagation();
                    e.preventDefault();
                    events.fire('walk.vertex.remove', v);
                }
                return;
            }
            if (e.button !== 0) return;
            e.stopPropagation();
            e.preventDefault();

            if (mode === 'floor') {
                const hit = await pickSplat(e.clientX, e.clientY);
                if (hit) events.fire('walk.floor.pick', hit);
                else events.fire('walk.status', 'No splat there — click on the visible floor.');
                return;
            }

            if (mode === 'level-height') {
                const hit = await pickSplat(e.clientX, e.clientY);
                const wa = walk();
                if (hit && wa) events.fire('walk.level.height', wa.activeLevel, hit.y);
                else events.fire('walk.status', 'No splat there — click a point ON that floor.');
                return;
            }

            if (mode === 'stairs') {
                const hit = pickLevelPoint(e.clientX, e.clientY);
                if (hit) events.fire('walk.stairs.pick', hit.level, hit.point);
                else events.fire('walk.status', 'Click INSIDE a floor boundary — each side of the flight runs from one floor to another.');
                return;
            }

            // BOUNDARY mode — record the press; decide add-vs-drag on move/up.
            const wa = walk();
            if (!wa) return;
            if (!wa.activePlane()) {
                events.fire('walk.status', 'Set this level’s height first — click a point on that floor, or type it.');
                return;
            }
            const rect = canvas.getBoundingClientRect();
            pointerDown = true;
            dragging = false;
            downX = e.clientX;
            downY = e.clientY;
            candidateVertex = wa.nearestVertex(e.clientX - rect.left, e.clientY - rect.top, VERTEX_GRAB_PX);
            candidateCorner = candidateVertex < 0 ?
                wa.nearestStairsCorner(e.clientX - rect.left, e.clientY - rect.top, VERTEX_GRAB_PX) :
                null;
            try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
        };

        const onPointerMove = (e: PointerEvent) => {
            if (!active) return;

            // Stairs drafting: preview the next pick point on whichever floor
            // the cursor is over (dashed line from the last pick in WalkArea).
            // With no pick yet there is nothing to anchor a line to — send
            // null so the BOUNDARY preview doesn't draw in stairs mode.
            if (mode === 'stairs') {
                const drafting = (walk()?.stairsDraft.length ?? 0) > 0;
                events.fire('walk.preview', drafting ? (pickLevelPoint(e.clientX, e.clientY)?.point ?? null) : null);
                return;
            }
            if (mode !== 'boundary') return;

            // Mid-press near a vertex or stairs corner: promote to a drag once the pointer moves.
            if (pointerDown && (candidateVertex >= 0 || candidateCorner)) {
                if (!dragging && Math.hypot(e.clientX - downX, e.clientY - downY) > DRAG_THRESH) {
                    dragging = true;
                }
                if (dragging && candidateCorner) {
                    const corner = walk()?.stairsCorner(candidateCorner.stairs, candidateCorner.corner);
                    const hit = corner ? hitHeight(e.clientX, e.clientY, corner.y) : null;
                    if (hit) events.fire('walk.stairs.corner', candidateCorner.stairs, candidateCorner.corner, hit.x, hit.z);
                } else if (dragging) {
                    const hit = hitPlane(e.clientX, e.clientY);
                    if (hit) events.fire('walk.vertex.move', candidateVertex, hit);
                }
                return;
            }
            if (!pointerDown) {
                const rect = canvas.getBoundingClientRect();
                hoverVertex = walk()?.nearestVertex(e.clientX - rect.left, e.clientY - rect.top, VERTEX_GRAB_PX) ?? -1;
            }

            // Otherwise preview where the next vertex would land (null if grazing).
            events.fire('walk.preview', hitPlane(e.clientX, e.clientY));
        };

        const onPointerUp = (e: PointerEvent) => {
            if (active && mode === 'boundary' && pointerDown && !dragging && !candidateCorner) {
                // A click (no drag) adds a vertex on the plane, into the
                // edge under the cursor when there is one.
                const hit = hitPlane(e.clientX, e.clientY);
                const rect = canvas.getBoundingClientRect();
                const edge = candidateVertex < 0 ?
                    (walk()?.nearestEdge(e.clientX - rect.left, e.clientY - rect.top, EDGE_GRAB_PX) ?? -1) :
                    -1;
                if (hit && edge >= 0) events.fire('walk.vertex.insert', edge, hit);
                else if (hit) events.fire('walk.vertex.add', hit);
                else events.fire('walk.status', 'Too shallow an angle — look down more at the floor, then click.');
            }
            pointerDown = false;
            dragging = false;
            candidateVertex = -1;
            candidateCorner = null;
            try { canvas.releasePointerCapture(e.pointerId); } catch (err) { /* ignore */ }
        };

        const onPointerLeave = () => {
            if (active) events.fire('walk.preview', null);
        };

        const onContextMenu = (e: MouseEvent) => { if (active) e.preventDefault(); };

        const onKeyDown = (e: KeyboardEvent) => {
            if (!active) return;
            if (e.key === 'Escape') {
                e.stopImmediatePropagation();
                if (mode === 'stairs' || mode === 'level-height') {
                    // Esc backs out of a sub-flow, not the whole tool.
                    setMode('boundary');
                } else {
                    events.fire('tool.deactivate');
                }
            } else if ((e.key === 'Backspace' || e.key === 'Delete') &&
                       (mode === 'boundary' || mode === 'stairs')) {
                // stopImmediatePropagation (not just stopPropagation) so the
                // global 'select.delete' shortcut — also bound to Backspace/
                // Delete, registered on `document` — never fires. We listen in
                // the CAPTURE phase on window (see activate) so this runs first.
                e.stopImmediatePropagation();
                e.preventDefault();
                if (mode === 'stairs') events.fire('walk.stairs.undoPick');
                else if (hoverVertex >= 0) events.fire('walk.vertex.remove', hoverVertex);
                else events.fire('walk.vertex.removeLast');
                hoverVertex = -1;
            }
        };

        // When WalkArea finishes the plane, advance to boundary drawing.
        events.on('walk.floor.set', () => { if (active) setMode('boundary'); });
        // Re-picking the floor drops us back into floor mode.
        events.on('walk.floor.reset', () => { if (active) setMode('floor'); });
        // A freshly added level needs its height picked first.
        events.on('walk.level.added', () => { if (active) setMode('level-height'); });
        // Height set (picked or typed) → draw that level's boundary.
        events.on('walk.level.heightSet', () => {
            if (active && mode === 'level-height') setMode('boundary');
        });
        // Panel starts a stairs draft; the 4th pick completes it.
        events.on('walk.stairs.begin', () => { if (active) setMode('stairs'); });
        events.on('walk.stairs.complete', () => { if (active) setMode('boundary'); });
        // Selecting another level mid-flow: adjust the mode to its state.
        events.on('walk.level.select', () => {
            if (active && mode !== 'floor' && mode !== 'stairs') setMode(initialMode());
        });

        const resetPress = () => {
            pointerDown = false; dragging = false; candidateVertex = -1; candidateCorner = null; hoverVertex = -1;
        };

        this.activate = () => {
            active = true;
            resetPress();
            canvas.style.cursor = 'crosshair';
            canvas.addEventListener('pointerdown', onPointerDown, true);
            canvas.addEventListener('pointermove', onPointerMove);
            canvas.addEventListener('pointerup', onPointerUp);
            canvas.addEventListener('pointerleave', onPointerLeave);
            canvas.addEventListener('contextmenu', onContextMenu);
            // Capture phase (window) so this beats the global 'select.delete'
            // shortcut's document-level listener to the Backspace/Delete key.
            window.addEventListener('keydown', onKeyDown, true);
            events.fire('walk.panel.show');
            // Start in whichever mode makes sense for the authoring state.
            setMode(initialMode());
            events.fire('walk.tool.active', true);
        };

        this.deactivate = () => {
            active = false;
            resetPress();
            canvas.style.cursor = '';
            canvas.removeEventListener('pointerdown', onPointerDown, true);
            canvas.removeEventListener('pointermove', onPointerMove);
            canvas.removeEventListener('pointerup', onPointerUp);
            canvas.removeEventListener('pointerleave', onPointerLeave);
            canvas.removeEventListener('contextmenu', onContextMenu);
            window.removeEventListener('keydown', onKeyDown, true);
            events.fire('walk.stairs.cancel');
            events.fire('walk.preview', null);
            events.fire('walk.tool.active', false);
        };
    }
}

export { WalkTool };
